import { useMemo } from 'react'
import styled from 'styled-components'

// ---------------------------------------------------------------------------
// Styled container
// ---------------------------------------------------------------------------
const Container = styled.div`
    font-size: ${({ $fontSize }) => $fontSize || '0.875rem'};
    line-height: 1.5;
    color: ${({ theme }) => theme.form?.font || 'inherit'};
    overflow: hidden;
    ${({ $maxLines }) => $maxLines ? `
        display: -webkit-box;
        -webkit-line-clamp: ${$maxLines};
        -webkit-box-orient: vertical;
    ` : ''}

    p  { margin: 0 0 0.2rem 0; }
    h1 { font-size: 1.4em; font-weight: bold; margin: 0.3rem 0 0.15rem; }
    h2 { font-size: 1.2em; font-weight: bold; margin: 0.3rem 0 0.15rem; }
    h3 { font-size: 1.05em; font-weight: bold; margin: 0.3rem 0 0.15rem; }
    ul { padding-left: 1.25rem; margin: 0.2rem 0; list-style-type: disc; }
    ol { padding-left: 1.25rem; margin: 0.2rem 0; list-style-type: decimal; }
    li { display: list-item; }
    blockquote {
        border-left: 3px solid ${({ theme }) => theme.form?.border || '#ccc'};
        padding-left: 0.6rem;
        margin: 0.2rem 0;
        opacity: 0.8;
    }
    code {
        font-family: monospace;
        font-size: 0.9em;
        background: rgba(0, 0, 0, 0.06);
        padding: 0 3px;
        border-radius: 3px;
    }
    a { color: ${({ theme }) => theme.font?.highlight || 'inherit'}; text-decoration: underline; }
`

// ---------------------------------------------------------------------------
// Lexical text format bitmask constants
// ---------------------------------------------------------------------------
const BOLD          = 1
const ITALIC        = 2
const STRIKETHROUGH = 4
const UNDERLINE     = 8
const CODE          = 16

// Lexical element format → CSS text-align
// 0 = left (default), 1 = center, 2 = right, 3 = justify, 4 = start, 5 = end
const ALIGN_MAP = { 1: 'center', 2: 'right', 3: 'justify', 4: 'start', 5: 'end' }

// Parse a Lexical inline CSS string (e.g. "color: red; font-size: 1.2em;")
// into a React style object.
function parseCssString(css) {
    if (!css) return {}
    const style = {}
    css.split(';').forEach(part => {
        const colon = part.indexOf(':')
        if (colon === -1) return
        const prop = part.slice(0, colon).trim()
        const val  = part.slice(colon + 1).trim()
        if (!prop || !val) return
        const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase())
        style[camel] = val
    })
    return style
}

// Build a React element for a text node, merging the format bitmask and
// any inline CSS styles (color, font-size, etc.) set via $patchStyleText.
function applyFormat(format, cssStyle, text, key) {
    const style = parseCssString(cssStyle)

    if (format & BOLD)        style.fontWeight = 'bold'
    if (format & ITALIC)      style.fontStyle  = 'italic'

    const hasUnder  = !!(format & UNDERLINE)
    const hasStrike = !!(format & STRIKETHROUGH)
    if (hasUnder && hasStrike) style.textDecoration = 'underline line-through'
    else if (hasUnder)         style.textDecoration = 'underline'
    else if (hasStrike)        style.textDecoration = 'line-through'

    if (!Object.keys(style).length) return text

    if (format & CODE) return <code key={key} style={style}>{text}</code>
    return <span key={key} style={style}>{text}</span>
}

function renderNode(node, key) {
    const children = node.children?.map((child, i) => renderNode(child, i)) ?? null

    switch (node.type) {
        case 'text':
            return applyFormat(node.format || 0, node.style || '', node.text, key)

        case 'paragraph': {
            const align = ALIGN_MAP[node.format]
            return <p key={key} style={align ? { textAlign: align } : undefined}>{children}</p>
        }

        case 'heading': {
            const Tag = node.tag || 'h2'
            const align = ALIGN_MAP[node.format]
            return <Tag key={key} style={align ? { textAlign: align } : undefined}>{children}</Tag>
        }

        case 'list':
            return node.listType === 'number'
                ? <ol key={key}>{children}</ol>
                : <ul key={key}>{children}</ul>

        case 'listitem': {
            const align = ALIGN_MAP[node.format]
            return <li key={key} style={align ? { textAlign: align } : undefined}>{children}</li>
        }

        case 'link':
            return (
                <a key={key} href={node.url} target="_blank" rel="noreferrer">
                    {children}
                </a>
            )

        case 'quote':
            return <blockquote key={key}>{children}</blockquote>

        case 'root':
            return <>{children}</>

        default:
            return children
    }
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------
export default function RichTextDisplay({ value, maxLines, fontSize }) {
    const content = useMemo(() => {
        if (!value) return null
        try {
            const parsed = JSON.parse(value)
            return renderNode(parsed.root)
        } catch {
            return null
        }
    }, [value])

    if (!content) return null

    return (
        <Container $maxLines={maxLines} $fontSize={fontSize}>
            {content}
        </Container>
    )
}
